import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors gracefully (don't logout)
    if (!error.response) {
      console.warn('Network error or timeout:', error.message);
      return Promise.reject(error);
    }
    
    // Only clear auth and redirect on explicit token expiration or invalid token
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || '';
      const isTokenError = errorDetail.includes('expired') || 
                          errorDetail.includes('Invalid token') ||
                          errorDetail.includes('Not authenticated');
      
      // Don't logout for /auth/me failures during initial load
      const isAuthCheck = error.config.url.includes('/auth/me');
      
      // Only force logout if it's a clear token issue (not just a transient 401)
      if (isTokenError && !isAuthCheck) {
        console.warn('Auth token expired or invalid, logging out');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  setPassword: (token, password) => api.post('/auth/set-password', { token, password }),
  me: () => api.get('/auth/me'),
  // 2FA APIs
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (totp_code) => api.post('/auth/2fa/verify', { totp_code }),
  disable2FA: (password, totp_code) => api.post('/auth/2fa/disable', { password, totp_code }),
  get2FAStatus: () => api.get('/auth/2fa/status'),
};

// Admin APIs
export const adminAPI = {
  // Users
  createUser: (data) => api.post('/admin/users', data),
  listUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  resendInvite: (id) => api.post(`/admin/users/${id}/resend-invite`),
  revoke2FA: (id) => api.post(`/admin/users/${id}/revoke-2fa`),
  
  // Analytics
  getAnalytics: (period = 30) => api.get('/admin/analytics', { params: { period } }),
  
  // Cities
  createCity: (data) => api.post('/admin/cities', data),
  listCities: () => api.get('/admin/cities'),
  updateCity: (id, data) => api.patch(`/admin/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/admin/cities/${id}`),
  
  // Categories
  createCategory: (data) => api.post('/admin/categories', data),
  listCategories: () => api.get('/admin/categories'),
  updateCategory: (id, data) => api.patch(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  
  // Monuments
  createMonument: (data) => api.post('/admin/monuments', data),
  listMonuments: (params) => api.get('/admin/monuments', { params }),
  updateMonument: (id, data) => api.patch(`/admin/monuments/${id}`, data),
  deleteMonument: (id) => api.delete(`/admin/monuments/${id}`),
  
  // Monument Investments
  createInvestment: (data) => api.post('/admin/investments', data),
  listInvestments: () => api.get('/admin/investments'),
  getInvestment: (monumentId) => api.get(`/admin/investments/${monumentId}`),
  updateInvestment: (monumentId, data) => api.patch(`/admin/investments/${monumentId}`, data),
  
  // Member Shares
  createMemberShare: (data) => api.post('/admin/member-shares', data),
  listMemberShares: (params) => api.get('/admin/member-shares', { params }),
  updateMemberShare: (id, data) => api.patch(`/admin/member-shares/${id}`, data),
  deleteMemberShare: (id) => api.delete(`/admin/member-shares/${id}`),
  
  // P&L Reports
  getPLReport: (monumentId, monthKey) => api.get(`/admin/pl-report/${monumentId}`, { params: { month_key: monthKey } }),
  
  // Ticket Slots
  createTicketSlot: (data) => api.post('/admin/ticket-slots', data),
  listTicketSlots: (params) => api.get('/admin/ticket-slots', { params }),
  updateTicketSlot: (id, data) => api.patch(`/admin/ticket-slots/${id}`, data),
  deleteTicketSlot: (id) => api.delete(`/admin/ticket-slots/${id}`),
  deleteSlotTicketFiles: (id) => api.post(`/admin/ticket-slots/${id}/clear-files`),
  getTicketSlotStats: (id) => api.get(`/admin/ticket-slots/${id}/stats`),
  
  // Ticket Files
  uploadTicketFiles: (slotId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/admin/ticket-files/upload?slot_id=${slotId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listTicketFiles: (params) => api.get('/admin/ticket-files', { params }),
  voidTicketFile: (id) => api.patch(`/admin/ticket-files/${id}/void`),
  
  // Pricing Rules
  createPricingRule: (data) => api.post('/admin/pricing-rules', data),
  listPricingRules: (params) => api.get('/admin/pricing-rules', { params }),
  deletePricingRule: (id) => api.delete(`/admin/pricing-rules/${id}`),
  
  // Guide Management
  createGuideProfile: (data) => api.post('/admin/guide-profiles', data),
  updateGuideProfile: (id, data) => api.patch(`/admin/guide-profiles/${id}`, data),
  assignGuideCityCategory: (data) => api.post('/admin/guide-city-categories', data),
  listGuideCityCategories: (params) => api.get('/admin/guide-city-categories', { params }),
  deleteGuideCityCategory: (id) => api.delete(`/admin/guide-city-categories/${id}`),
  
  // Ledger
  createAdjustment: (userId, amount, note) => 
    api.post(`/admin/ledger/adjustment?user_id=${userId}&amount_eur_cents=${amount}&note=${encodeURIComponent(note)}`),
  recordPayment: (userId, amount, note) => 
    api.post(`/admin/ledger/payment?user_id=${userId}&amount_eur_cents=${amount}&note=${encodeURIComponent(note)}`),
  listLedger: (params) => api.get('/admin/ledger', { params }),
  
  // Reports
  getDownloadsReport: (params) => api.get('/admin/reports/downloads', { params }),
  getBalancesReport: (params) => api.get('/admin/reports/balances', { params }),
  getGuideRequestsReport: (params) => api.get('/admin/reports/guide-requests', { params }),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  getNotificationsLog: (params) => api.get('/admin/notifications-log', { params }),
  
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard-stats'),
  
  // Email Templates
  getEmailTemplates: () => api.get('/admin/email-templates'),
  getEmailTemplate: (templateKey) => api.get(`/admin/email-templates/${templateKey}`),
  updateEmailTemplate: (templateKey, data) => api.put(`/admin/email-templates/${templateKey}`, data),
  previewEmailTemplate: (templateKey) => api.post(`/admin/email-templates/${templateKey}/preview`),
  sendTestEmail: (templateKey, testEmail) => 
    api.post(`/admin/email-templates/${templateKey}/send-test?test_email=${encodeURIComponent(testEmail)}`),
  
  // Send Emails
  sendDailySummaries: () => api.post('/admin/send-daily-summaries'),
  sendMonthlyInvoices: () => api.post('/admin/send-monthly-invoices'),
  sendWeeklyPaymentReminders: () => api.post('/admin/send-weekly-payment-reminders'),
  sendPnlReport: (monumentId, monthKey, userIds = null) => 
    api.post('/admin/send-pnl-report', { 
      monument_id: monumentId, 
      month_key: monthKey, 
      user_ids: userIds 
    }),
  
  // Guide Applications
  getGuideApplications: (params) => api.get('/admin/guide-applications', { params }),
  approveGuideApplication: (id, force = false) => api.post(`/admin/guide-applications/${id}/approve?force=${force}`),
  rejectGuideApplication: (id, reason) => api.post(`/admin/guide-applications/${id}/reject?reason=${encodeURIComponent(reason)}`),
  verifyApplicationEmail: (id) => api.post(`/admin/guide-applications/${id}/verify-email`),
  
  // SMS
  getSmsStatus: () => api.get('/admin/sms-status'),
  testSms: (phone) => api.post('/admin/test-sms', { phone }),
};

// Public/User APIs
export const publicAPI = {
  listCities: () => api.get('/public/cities'),
  listCategories: () => api.get('/public/categories'),
  listMonuments: (params) => api.get('/public/monuments', { params }),
};

// Ticket APIs (for users)
export const ticketAPI = {
  search: (params) => api.get('/user/tickets/search', { params }),
  download: (slotId, quantity = 1) => api.post(`/user/tickets/download/${slotId}?quantity=${quantity}`),
  getFile: (token) => `${API_BASE}/user/tickets/file/${token}`,
};

// User APIs
export const userAPI = {
  getDownloads: () => api.get('/user/downloads'),
  getStatement: (monthKey) => api.get('/user/statement', { params: { month_key: monthKey } }),
  getLedger: (monthKey) => api.get('/user/ledger', { params: { month_key: monthKey } }),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  changePassword: (currentPassword, newPassword) => api.post('/user/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
};

// Member Investment APIs
export const memberInvestmentAPI = {
  getInvestments: () => api.get('/member/investments'),
  getInvestmentPL: (monumentId, monthKey) => api.get(`/member/investments/${monumentId}/pl`, {
    params: { month_key: monthKey }
  }),
  getInvestmentHistory: (monumentId, months = 12) => api.get(`/member/investments/${monumentId}/history`, {
    params: { months }
  }),
};

// Payment APIs
export const paymentAPI = {
  createSession: (guideRequestId, originUrl) => api.post('/payments/create-session', { 
    guide_request_id: guideRequestId, 
    origin_url: originUrl 
  }),
  createWeeklySession: (guideRequestIds, originUrl) => api.post('/payments/create-weekly-session', {
    guide_request_ids: guideRequestIds,
    origin_url: originUrl
  }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getTransaction: (guideRequestId) => api.get(`/payments/transaction/${guideRequestId}`),
  getStripeConfig: () => api.get('/stripe/config'),
  getPendingOnlineTours: () => api.get('/member/pending-online-tours'),
};

// Stripe Connect APIs (for guides)
export const stripeConnectAPI = {
  createAccount: () => api.post('/guide/stripe/connect'),
  getStatus: () => api.get('/guide/stripe/status'),
  getDashboardLink: () => api.get('/guide/stripe/dashboard-link'),
};

// Guide Availability APIs
export const guideAvailabilityAPI = {
  getAvailability: () => api.get('/guide/availability'),
  updateAvailability: (data) => api.put('/guide/availability', data),
  addException: (exception) => api.post('/guide/availability/exception', exception),
  removeException: (date) => api.delete(`/guide/availability/exception/${date}`),
  getPublicAvailability: (guideId, month) => api.get(`/guides/${guideId}/availability`, { params: { month } }),
};

// Member APIs
export const memberAPI = {
  createGuideRequest: (data) => api.post('/member/guide-requests', data),
  getGuideRequests: (params) => api.get('/member/guide-requests', { params }),
  cancelGuideRequest: (id) => api.patch(`/member/guide-requests/${id}/cancel`),
  getFinance: () => api.get('/member/finance'),
  uploadExperienceAttachments: (requestId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/member/guide-requests/${requestId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadProofPhoto: async (filename) => {
    const response = await api.get(`/proof-photos/${filename}`, {
      responseType: 'blob'
    });
    // Create a download link
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  viewProofPhoto: async (filename) => {
    const response = await api.get(`/proof-photos/${filename}`, {
      responseType: 'blob'
    });
    // Get the correct MIME type from the response or determine from extension
    const ext = filename.split('.').pop().toLowerCase();
    const mimeTypes = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp'
    };
    const mimeType = response.data.type || mimeTypes[ext] || 'image/jpeg';
    
    // Create blob with correct MIME type
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },
  viewExperienceAttachment: async (filename) => {
    const response = await api.get(`/experience-attachments/${filename}`, {
      responseType: 'blob'
    });
    const ext = filename.split('.').pop().toLowerCase();
    const mimeTypes = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'pdf': 'application/pdf'
    };
    const mimeType = response.data.type || mimeTypes[ext] || 'application/octet-stream';
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },
  downloadExperienceAttachment: async (filename, originalName) => {
    const response = await api.get(`/experience-attachments/${filename}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', originalName || filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Guide APIs
export const guideAPI = {
  getRequests: (params) => api.get('/guide/requests', { params }),
  acceptRequest: (id) => api.post(`/guide/requests/${id}/accept`),
  declineRequest: (id, note) => api.post(`/guide/requests/${id}/decline`, null, { params: { note } }),
  completeRequest: (id) => api.post(`/guide/requests/${id}/complete`),
  getHistory: () => api.get('/guide/history'),
  getFinance: () => api.get('/guide/finance'),
  uploadProofPhotos: (requestId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/guide/requests/${requestId}/upload-proof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getProfile: () => api.get('/guide/profile'),
  updateProfile: (data) => api.patch('/guide/profile', data),
  uploadProfilePhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/guide/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  viewExperienceAttachment: async (filename) => {
    const response = await api.get(`/experience-attachments/${filename}`, {
      responseType: 'blob'
    });
    const ext = filename.split('.').pop().toLowerCase();
    const mimeTypes = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'pdf': 'application/pdf'
    };
    const mimeType = response.data.type || mimeTypes[ext] || 'application/octet-stream';
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },
  downloadExperienceAttachment: async (filename, originalName) => {
    const response = await api.get(`/experience-attachments/${filename}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', originalName || filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Utility APIs
export const utilAPI = {
  seedAdmin: () => api.post('/seed-admin'),
  healthCheck: () => api.get('/health'),
};

export default api;
